import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import DatePicker from "react-date-picker";
import moment from "moment";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";

const EditTakhmeen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { accessToken, userData } = useSelector((s) => s.user);
    const takhmeenId = location.state?.takhmeen_id;
    const [record, setRecord] = useState(null);
    const [roles, setRoles] = useState([]);
    const [divisionMembers, setDivisionMembers] = useState([]);

    const memberOptions = useMemo(() => divisionMembers.map((m) => ({ value: m.user_id, label: `${m.full_name.trim()} (${m.user_its})` })), [divisionMembers]);

    useEffect(() => {
        if (!takhmeenId) { navigate("/takhmeen"); return; }
        dispatch(setPageTitle("Edit Takhmeen"));
        dispatch(setMainLoading(true));
        PostAxios("takhmeen/view-takhmeen", { takhmeen_id: takhmeenId }, accessToken).then(({ data }) => {
            if (data.status === "1") {
                setRecord(data.data);
                setRoles(data.roles || []);
                PostAxios("takhmeen/division-members", { org_id: data.data.org_id, div_id: data.data.div_id }, accessToken)
                    .then(({ data: dm }) => { if (dm.status === "1") setDivisionMembers(dm.list || []); });
            } else navigate("/takhmeen");
        }).finally(() => dispatch(setMainLoading(false)));
    }, [takhmeenId]);

    if (!record) return null;

    const finances = roles.filter((r) => r.role_type === "finance_incharge");
    const projects = roles.filter((r) => r.role_type === "project_incharge");
    const coords = roles.filter((r) => r.role_type === "coordinator");

    return (
        <div className="main-form-sec">
            <Formik enableReinitialize initialValues={{
                takhmeen_name: record.takhmeen_name,
                description: record.description || "",
                start_date: new Date(record.start_date),
                end_date: new Date(record.end_date),
                base_amount: record.base_amount,
                status: parseInt(record.status, 10),
                finance_incharges: finances.map((f) => ({ value: f.user_id, label: f.full_name?.trim() })),
                project_incharges: projects.map((p) => ({ value: p.user_id, label: p.full_name?.trim() })),
                coordinators: coords.map((c) => ({ value: c.user_id, label: c.full_name?.trim() })),
            }}
                onSubmit={(values, { setSubmitting }) => {
                    dispatch(setMainLoading(true));
                    PostAxios("takhmeen/update-takhmeen", {
                        takhmeen_id: takhmeenId,
                        takhmeen_name: values.takhmeen_name,
                        description: values.description,
                        start_date: moment(values.start_date).format("YYYY-MM-DD"),
                        end_date: moment(values.end_date).format("YYYY-MM-DD"),
                        base_amount: values.base_amount,
                        status: values.status,
                        finance_incharge_ids: JSON.stringify((values.finance_incharges || []).map((c) => c.value)),
                        project_incharge_ids: JSON.stringify((values.project_incharges || []).map((c) => c.value)),
                        coordinator_ids: JSON.stringify((values.coordinators || []).map((c) => c.value)),
                    }, accessToken).then(({ data }) => {
                        if (data.status === "1") {
                            dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                            navigate("/takhmeen/view", { state: { takhmeen_id: takhmeenId } });
                        } else dispatch(setToastData({ message: data.errors ? ConvertErrorArrayToString(data.errors) : data.message, type: "error", showCloseBtn: true }));
                    }).finally(() => { setSubmitting(false); dispatch(setMainLoading(false)); });
                }}
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <div className="form-sec">
                            <div className="form-field-group w-50"><div className="w-50"><label>Name*</label></div><div className="w-50"><Field name="takhmeen_name" className="form-input" /></div></div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Description</label></div><div className="w-50"><Field as="textarea" name="description" className="form-input" rows={3} /></div></div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Amount*</label></div><div className="w-50"><Field name="base_amount" type="number" className="form-input" onWheel={(e) => e.currentTarget.blur()} /></div></div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Start*</label></div>
                                <div className="w-50">
                                    <div className="date-picker-sec">
                                        <DatePicker
                                            value={values.start_date}
                                            onChange={(d) => setFieldValue("start_date", d)}
                                            clearIcon={null}
                                            format="dd-MM-y"
                                            dayPlaceholder="DD"
                                            monthPlaceholder="MM"
                                            yearPlaceholder="YYYY"
                                            className="form-input" />
                                    </div>
                                </div>
                            </div>
                            <div className="form-field-group w-50"><div className="w-50"><label>End*</label></div>
                                <div className="w-50">
                                    <div className="date-picker-sec">
                                        <DatePicker
                                            value={values.end_date}
                                            onChange={(d) => setFieldValue("end_date", d)}
                                            clearIcon={null}
                                            format="dd-MM-y"
                                            dayPlaceholder="DD"
                                            monthPlaceholder="MM"
                                            yearPlaceholder="YYYY"
                                            className="form-input" />
                                    </div>
                                </div>
                            </div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Status</label></div><div className="w-50"><Field as="select" name="status" className="select-input"><option value={1}>Active</option><option value={0}>Inactive</option></Field></div></div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Finance Incharge</label></div><div className="w-50"><Select isMulti options={memberOptions} value={values.finance_incharges} onChange={(v) => setFieldValue("finance_incharges", v || [])} /></div></div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Project Incharge</label></div><div className="w-50"><Select isMulti options={memberOptions} value={values.project_incharges} onChange={(v) => setFieldValue("project_incharges", v || [])} /></div></div>
                            <div className="form-field-group w-50"><div className="w-50"><label>Coordinators</label></div><div className="w-50"><Select isMulti options={memberOptions} value={values.coordinators} onChange={(v) => setFieldValue("coordinators", v || [])} /></div></div>
                        </div>
                        <div className="w-100 d-flex justify-content-start align-items-center">
                            <button type="submit" className="btn btn-blue" disabled={isSubmitting}>Update</button>
                            <button type="button" className="btn btn-blue ms-2" onClick={() => navigate("/takhmeen/view", { state: { takhmeen_id: takhmeenId } })}>Cancel</button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EditTakhmeen;
